import React, { useState } from "react";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import dayjs from "dayjs";
import { Tag, Tooltip } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Table from "../../../../../../Common/Table";
import {
  MeetingGetParams,
  MeetingTableRow,
  useMeetingTableGet,
} from "../../../../../../../hooks/api/meetings";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { getSorterParams } from "../../../../../../../utils";
import { SortOrder } from "antd/es/table/interface";
import { RequestData } from "@ant-design/pro-components";
import { useVaadTabs } from "../../context";

interface PastMeetingsTableProps {
  onTotalChange?: (total: number) => void;
}

const RecordingTypeDisplay = {
  none: { text: "None", isTag: false },
  video: {
    text: "Video",
    color: "blue",
    bg: "#e6f4ff",
    border: "#91caff",
    textColor: "#0958d9",
  },
  audio: {
    text: "Audio",
    color: "green",
    bg: "#f6ffed",
    border: "#b7eb8f",
    textColor: "#389e0d",
  },
} as const;

export default function PastMeetingsTable({
  onTotalChange,
}: PastMeetingsTableProps): React.ReactNode | null {
  const navigate = useNavigate();
  const { id } = useParams();
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  const { t, i18n } = useTranslation();
  const meetingTableGet = useMeetingTableGet();
  const { middahOptions, setMeetingsSubTab } = useVaadTabs();

  const pastColumns = (): ProColumns<MeetingTableRow>[] => [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      // sorter: true,
      hideInSearch: true,
    },
    {
      title: "Date",
      dataIndex: "startTime",
      key: "startTime",
      sorter: true,
      hideInSearch: true,
      render: (_: any, record: MeetingTableRow) => {
        const date = dayjs(record.startTimeLocal);
        return date.format("MMM D, YYYY, ddd");
      },
    },
    {
      title: "Middah",
      dataIndex: "middahIds",
      key: "middahIds",
      sorter: true,
      valueType: "select",
      valueEnum: Object.fromEntries(
        middahOptions.map((m) => [m.value, { text: m.label }]),
      ),
      render: (_, record: MeetingTableRow) => (
        <span>{record.middah?.name || "-"}</span>
      ),
    },
    {
      title: "Recording",
      dataIndex: "recordings",
      key: "recordingType",
      hideInSearch: true,
      render: (_: any, record: MeetingTableRow) => {
        const recordings = record.recordings || [];

        if (recordings.length === 0) {
          return <span>None</span>;
        }

        const hasVideo = recordings.some((r) => r.type === "video");
        const hasAudio = recordings.some((r) => r.type === "audio");

        if (!hasVideo && !hasAudio) {
          return <span>None</span>;
        }

        return (
          <span style={{ display: "flex", gap: 8 }}>
            {hasVideo && (
              <Tag
                style={{
                  borderRadius: 4,
                  border: `1px solid ${RecordingTypeDisplay.video.border}`,
                  background: RecordingTypeDisplay.video.bg,
                  color: RecordingTypeDisplay.video.textColor,
                }}
              >
                {RecordingTypeDisplay.video.text}
              </Tag>
            )}
            {hasAudio && (
              <Tag
                style={{
                  borderRadius: 4,
                  border: `1px solid ${RecordingTypeDisplay.audio.border}`,
                  background: RecordingTypeDisplay.audio.bg,
                  color: RecordingTypeDisplay.audio.textColor,
                }}
              >
                {RecordingTypeDisplay.audio.text}
              </Tag>
            )}
          </span>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      hideInSearch: true,
      render: (_, record: MeetingTableRow) => {
        const description = record.description || "-";
        return (
          <Tooltip
            title={description}
            overlayStyle={{ maxWidth: 500 }}
            placement="topLeft"
          >
            <span
              style={{
                display: "inline-block",
                maxWidth: 200,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {description}
            </span>
          </Tooltip>
        );
      },
    },
  ];

  const tableRequest = (
    {
      current,
      pageSize,
      tab,
      ...args
    }: Record<string, string> & {
      pageSize?: number;
      current?: number;
      keyword?: string;
      tab?: string;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<MeetingTableRow>>> => {
    const newParams: MeetingGetParams = {
      period: "past",
      vaadId: id,
      page: current || 1,
      orderByColumn: "startTime",
      orderBy: "ASC",
      pageSize: pageSize || 10,
      ...args,
      ...getSorterParams(sorter),
    };

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

    return meetingTableGet.fetch(newParams).then((response) => {
      if (response) {
        const { data, total } = response;
        onTotalChange?.(response.total);

        return {
          data: data || [],
          success: true,
          total,
        };
      }

      return { data: [], success: false, total: 0 };
    });
  };

  return (
    <>
      <Table<MeetingTableRow>
        key={`past-${tableKey}-${i18n.language}`}
        // Future/Past tables share the same URL; don't sync pagination to it,
        // otherwise a stale current=N leaks across the Segmented sub-tabs.
        form={{ syncToUrl: false }}
        resetKey={() => setTableKey(Date.now())}
        className="tableCustomPadding"
        headerTitle={t("pages.meetings.past")}
        columns={pastColumns()}
        request={tableRequest}
        onReset={() => {
          setSearchParams({}, { replace: true });
          setTableKey(Date.now());
        }}
        actions={[
          [
            "edit",
            ({ record }) => ({
              title: "Edit",
              onClick: (e: MouseEvent) => {
                e.stopPropagation();
                navigate(`${record.id}/edit`, { state: { mode: "edit-past" } });
                setMeetingsSubTab("past");
              },
            }),
          ],
        ]}
        search={{
          style: {
            // display: "none",
            paddingBottom: 0,
          },
        }}
        options={{
          search: {
            name: "search",
            placeholder: "Search by meeting name",
            style: { width: 280 },
          },
        }}
        columnsState={{
          persistenceKey: "pro-table-past-meetings",
          persistenceType: "localStorage",
        }}
      />
    </>
  );
}
